import React, { useContext } from 'react'
import styled, { DefaultTheme } from 'styled-components'
import PropTypes from 'prop-types'
import {
  borderRadius,
  borderRadiusSizes,
  colorConstants,
  colors,
  gutterSize,
  margin,
  marginSizes,
  padding,
  paddingSizes
} from '@jarvis.oddle/core/theme/default'
import breakpoint from 'src/utils/styledComponentsBreakpoint'
import { Column, Row } from '@jarvis.oddle/core/components/Grid'
import Card from '@jarvis.oddle/core/components/Card'
import Div from '@jarvis.oddle/core/components/Div'
import Flex from '@jarvis.oddle/core/components/Flex'
import { H2, H4, LinkText, P } from '@jarvis.oddle/core/components/Typo'
import Form from '@jarvis.oddle/core/components/Form'
import IconBack from '@jarvis.oddle/core/components/Icons/IconBack'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'

export const Container = styled(Div)`
  padding-top: ${padding[paddingSizes.large]};
  ${breakpoint('desktop')`
    padding-top: ${padding[paddingSizes.medium]};
  `}
`

export const Section = styled(Div)`
  padding: ${padding[paddingSizes.medium]};
  ${breakpoint('desktop')`
    padding: ${padding[paddingSizes.large]} ${padding[paddingSizes.extraLarge]};
  `}
`

export const MainCard = styled(Card)`
  border-radius: ${borderRadius[borderRadiusSizes.small]};
  margin-bottom: ${margin[marginSizes.medium]};
  padding: 0 0 ${padding[paddingSizes.medium]} 0;
  ${(props) => (props.error ? 'height: 70vh;' : '')}
  ${breakpoint('desktop')`
    padding: 0 0 ${padding[paddingSizes.large]} 0;
  `}
`

export const CardGroup = styled(Row).attrs(() => ({
  gutterSize: gutterSize.mobile,
  marginBottom: marginSizes.small
}))``

export const CardHeader: React.FC<Typo.CommonPropTypes> = (
  props
): JSX.Element => (
  <H2
    marginTop={marginSizes.none}
    marginLeft={marginSizes.none}
    marginRight={marginSizes.none}
    {...props}
  />
)

interface CardLineProps {
  heading: string
  text: string
}

export const CardLine = ({ heading, text }: CardLineProps): JSX.Element => (
  <Column
    sm={12}
    md={6}
    gutterSize={gutterSize.mobile}
    marginBottom={marginSizes.small}
  >
    <P normalised>{heading}</P>
    <H4 normalised>{text}</H4>
  </Column>
)

CardLine.propTypes = {
  heading: PropTypes.string,
  text: PropTypes.string
}

const StyledCard = styled(Card)`
  border-radius: ${borderRadius[borderRadiusSizes.small]};
  margin-bottom: ${margin[marginSizes.medium]};
  padding: ${padding[paddingSizes.medium]};
  ${(props) => (props.error ? `height: 25vh;` : '')}
  ${breakpoint('desktop')`
    padding: ${padding[paddingSizes.large]} ${padding[paddingSizes.extraLarge]};
  `}
`

interface HeaderProps {
  headingText: string
  bodyText: string
  error: boolean
}

export const Header = ({
  headingText,
  bodyText,
  error = false
}: HeaderProps): JSX.Element => (
  <StyledCard error={error}>
    {!error && (
      <Flex flexDirection='column' justifyContent='center' alignItems='center'>
        <H4 normalised>{headingText}</H4>
        <H2 normalised textAlign='center'>
          {bodyText}
        </H2>
      </Flex>
    )}
  </StyledCard>
)

interface TextBoxProps {
  headingText: string
  bodyText: string
}

export const TextBox = ({
  headingText,
  bodyText
}: TextBoxProps): JSX.Element => (
  <Container>
    <Section>
      <H4>{headingText}</H4>
      <H4 normalised>{bodyText}</H4>
    </Section>
  </Container>
)

interface CheckBoxProps {
  name: string
  content: string | Record<string, unknown>
}

export const CheckBox = ({ name, content }: CheckBoxProps): JSX.Element => {
  return (
    <Form.Checkbox name={name}>
      {({ checked, disabled }) => (
        <React.Fragment>
          <Form.Checkbox.Tick checked={checked} disabled={disabled} />
          <P>{content}</P>
        </React.Fragment>
      )}
    </Form.Checkbox>
  )
}

const BackContainer = styled(Flex).attrs(() => ({
  flexDirection: 'row',
  alignItems: 'center',
  role: 'button'
}))`
  margin: ${margin[marginSizes.medium]} 0;
  display: none;
  cursor: pointer;
  ${breakpoint('desktop')`
    display: flex;
  `}
`

interface StyledIconBackProps {
  invert: boolean
  theme: DefaultTheme
}

const StyledIconBack = styled(IconBack).attrs(
  (
    props = {
      theme: {
        buttonTextColor: colorConstants.white,
        buttonColor: colors.brandPrimary
      }
    } as StyledIconBackProps
  ) => ({
    fill: props.invert ? props.theme.buttonColor : props.theme.buttonTextColor
  })
)``

interface StyledLinkTextProps {
  invert: boolean
  theme: DefaultTheme
}

const StyledLinkText = styled(LinkText).attrs(
  (
    props = {
      theme: {
        buttonTextColor: colorConstants.white,
        buttonColor: colors.brandPrimary
      }
    } as StyledLinkTextProps
  ) => ({
    color: props.invert ? props.theme.buttonColor : props.theme.buttonTextColor
  })
)`
  margin-left: ${margin[marginSizes.small]};
`

interface BackToCartProps {
  onClick: (x) => void
  invert?: boolean
  text: string
}

// TODO: consider using Router.beforePopState to implement this in the future
export const BackToCart = ({
  onClick,
  invert = false,
  text
}: BackToCartProps): JSX.Element => {
  const { t } = useContext(TranslationContext)
  return (
    <BackContainer onClick={onClick}>
      <StyledIconBack invert={invert} />
      <StyledLinkText invert={invert}>
        {text || t('Continue Shopping')}
      </StyledLinkText>
    </BackContainer>
  )
}

export const InputContainer = styled(Column).attrs(() => ({
  sm: 12,
  md: 6,
  gutterSize: gutterSize.mobile
}))``
